import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from 'react-query';
import TourCard from './components/TourCard';
import TourFilters from './components/TourFilters';
import VehicleFilterOptions from '../../components/Tours/VehicleFilterOptions';
import Pagination from '../../components/common/Pagination';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import SEOHead from '../../components/SEO/SEOHead';
import defaultSEO from '../../components/SEO/seoConfig';

const ToursListingPage = () => {
  const { id: categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: searchParams.get('category') ? searchParams.get('category').split(',') : [],
    duration: searchParams.get('duration') ? searchParams.get('duration').split(',') : [],
    price: [searchParams.get('minPrice') || 0, searchParams.get('maxPrice') || 10000],
    rating: searchParams.get('rating') || 0,
    search: searchParams.get('search') ? decodeURIComponent(searchParams.get('search')) : '',
    vehicle_type: searchParams.get('vehicle_type') ? searchParams.get('vehicle_type').split(',') : [],
    min_capacity: searchParams.get('min_capacity') || '',
    sort: searchParams.get('sort') || 'newest',
    page: parseInt(searchParams.get('page') || '1', 10),
  });
  
  const postsPerPage = 6;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Create a custom axios instance with the base URL
  const api = axios.create({
    baseURL: apiUrl
  });

  // Update filters when category ID from URL params changes
  useEffect(() => {
    if (categoryId) {
      setFilters(prev => ({
        ...prev,
        category: [categoryId]
      }));
    }
  }, [categoryId]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else {
          params.set(key, value);
        }
      }
    });
    
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Fetch tours data with React Query
  const {
    data: toursData,
    isLoading,
    isError,
    error
  } = useQuery(
    ['public-tours', filters],
    async () => {
      try {
        console.log('Fetching tours with filters:', filters);
        const response = await api.get('/api/v1/tours', {
          params: {
            category_id: filters.category.join(',') || undefined,
            min_price: filters.price[0] || undefined,
            max_price: filters.price[1] || undefined,
            search: filters.search || undefined,
            page: filters.page,
            per_page: postsPerPage,
            sort_by: getSortField(filters.sort),
            sort_order: getSortDirection(filters.sort),
            vehicle_type: filters.vehicle_type.join(',') || undefined,
            min_vehicle_capacity: filters.min_capacity || undefined,
          }
        });
        console.log('API response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching tours:', error);
        throw new Error(error.response?.data?.message || 'Failed to fetch tours');
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  );

  // Helper function to map frontend sort to backend sort_by
  const getSortField = (sort) => {
    switch (sort) {
      case 'price_low':
      case 'price_high':
        return 'price';
      case 'rating':
        return 'rating';
      case 'popularity':
        return 'reviews_count';
      case 'duration_low':
      case 'duration_high':
        return 'duration';
      case 'newest':
      default:
        return 'created_at';
    }
  };

  // Helper function to map frontend sort to backend sort_order
  const getSortDirection = (sort) => {
    switch (sort) {
      case 'price_low':
      case 'duration_low':
        return 'asc';
      case 'price_high':
      case 'rating':
      case 'popularity':
      case 'duration_high':
      case 'newest':
      default:
        return 'desc';
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (filterType === 'category' || filterType === 'duration' || filterType === 'vehicle_type') {
        // Handle array filters (checkboxes)
        const updatedArray = prevFilters[filterType].includes(value)
          ? prevFilters[filterType].filter(item => item !== value)
          : [...prevFilters[filterType], value];
        return { ...prevFilters, [filterType]: updatedArray, page: 1 };
      } else if (filterType === 'price') {
        // Handle price range
        return { ...prevFilters, price: value, page: 1 };
      } else if (filterType === 'min_capacity') {
        // Handle capacity filter
        return { ...prevFilters, min_capacity: value, page: 1 };
      } else {
        // Handle other filters
        return { ...prevFilters, [filterType]: value, page: 1 };
      }
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
    window.scrollTo(0, 0);
  };

  // Get tours and pagination data from API response
  const tours = toursData?.data?.data || [];
  const totalItems = toursData?.data?.meta?.total || 0;
  const totalPages = toursData?.data?.meta?.last_page || 1;

  // Fetch category data if categoryId is present
  const {
    data: categoryData
  } = useQuery(
    ['category', categoryId],
    async () => {
      if (!categoryId) return null;
      try {
        const response = await api.get(`/api/v1/categories/${categoryId}`);
        return response.data.data;
      } catch (error) {
        console.error('Error fetching category:', error);
        return null;
      }
    },
    {
      enabled: !!categoryId,
      staleTime: 300000 // 5 minutes
    }
  );

  // Generate SEO data based on filters and category
  const generateSEOData = () => {
    // Default title and description
    let title = 'Explore Our Tours & Safaris';
    let description = 'Discover the beauty of Kenya through our carefully curated tour packages. Find wildlife safaris, beach getaways, and mountain expeditions.';
    let keywords = ['tours', 'safaris', 'wildlife', 'beach', 'mountain', 'Kenya', 'travel', 'adventure'];
    let canonicalUrl = `${defaultSEO.siteUrl}/tours`;
    
    // If category is selected, customize SEO data
    if (categoryId && categoryData) {
      title = `${categoryData.name} Tours & Safaris`;
      description = categoryData.description || 
        `Explore our ${categoryData.name.toLowerCase()} tours and safari packages. Book your ${categoryData.name.toLowerCase()} adventure today!`;
      keywords = [categoryData.name.toLowerCase(), `${categoryData.name.toLowerCase()} tours`, `${categoryData.name.toLowerCase()} safaris`, ...keywords];
      canonicalUrl = `${defaultSEO.siteUrl}/tours/category/${categoryId}`;
    }
    
    // If search term is present, customize title
    if (filters.search) {
      title = `Tours & Safaris matching "${filters.search}"`;
      description = `Find the best tours and safari packages matching "${filters.search}". Book your adventure today!`;
      keywords = [filters.search, ...keywords];
    }
    
    // Add pagination info to canonical URL if not on first page
    if (filters.page > 1) {
      canonicalUrl += `?page=${filters.page}`;
    }
    
    // Generate structured data for tours listing
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: tours.map((tour, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: tour.title,
          description: tour.description?.substring(0, 100) || '',
          url: `${defaultSEO.siteUrl}/tours/${tour.slug}`,
          image: tour.featured_image || tour.images?.[0] || '',
          offers: {
            '@type': 'Offer',
            price: tour.price,
            priceCurrency: 'USD'
          }
        }
      }))
    };
    
    return {
      title,
      description,
      keywords,
      canonicalUrl,
      ogType: 'website',
      ogImage: categoryData?.image || `${defaultSEO.siteUrl}/images/tours-banner.jpg`,
      structuredData
    };
  };
  
  const seoData = generateSEOData();

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      {/* SEO Head */}
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl={seoData.canonicalUrl}
        ogType={seoData.ogType}
        ogImage={seoData.ogImage}
        structuredData={seoData.structuredData}
      />
      <div className="container-custom">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">Explore Our Tours</h1>
        <p className="text-gray-600 mb-8">
          Discover the beauty of Kenya through our carefully curated tour packages
        </p>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters sidebar */}
          <div className="lg:w-1/4">
            <TourFilters 
              filters={filters} 
              onFilterChange={(filterType, value) => handleFilterChange(filterType, value)} 
            />
            <div className="mt-4">
              <VehicleFilterOptions 
                selectedVehicleTypes={filters.vehicle_type}
                minCapacity={filters.min_capacity}
                onVehicleTypeChange={(value) => handleFilterChange('vehicle_type', value)}
                onCapacityChange={(value) => handleFilterChange('min_capacity', value)}
              />
            </div>
          </div>

          {/* Tours grid */}
          <div className="lg:w-3/4">
            {isLoading ? (
              <div className="flex justify-center items-center h-96">
                <LoadingSpinner />
              </div>
            ) : isError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error?.message || 'Failed to load tours. Please try again later.'}</span>
              </div>
            ) : (
              <>
                {tours.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <h3 className="text-xl font-semibold mb-2">No tours found</h3>
                    <p className="text-gray-600 mb-4">
                      We couldn't find any tours matching your criteria.
                    </p>
                    <button 
                      onClick={() => handleFilterChange('category', [])}
                      className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-2 rounded-md transition-colors"
                    >
                      Clear all filters
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 text-gray-600">
                      Showing {tours.length} of {totalItems} tours
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {tours.map(tour => (
                        <TourCard key={tour.id} tour={tour} />
                      ))}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="mt-12">
                        <Pagination
                          currentPage={filters.page}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToursListingPage;
